import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch, Redirect, useLocation } from "wouter";
import { useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { I18nProvider } from "./contexts/I18nProvider";
import Profile from "./pages/Profile";
import InviteAccept from "./pages/InviteAccept";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import AdminTeams from "./pages/AdminTeams";
import AdminHealth from "./pages/AdminHealth";
import PlatformConsole from "./pages/PlatformConsole";
import AdminHealthCenter from "./pages/AdminHealthCenter";
import OrgInviteAccept from "./pages/OrgInviteAccept";
import { GlobalDialogsProvider } from "./contexts/GlobalDialogs";
import { ActiveWorkspaceProvider } from "./contexts/ActiveWorkspace";
import { TabShell } from "./components/tabs/TabShell";
import Home from "./pages/Home";
import Team from "./pages/Team";
import TeamReport from "./pages/TeamReport";
import Drafts from "./pages/Drafts";
import YourWeek from "./pages/YourWeek";
import More from "./pages/More";

/** Five-tab routes (Phase 2). Stage-A cutover made `/` the tabbed Home
 *  experience. The legacy single-page Canvas (`/canvas`) was DELETED in the
 *  Stage B legacy cleanup (2026-06-14) — it is now an unrouted 404. */
function tabbed(Page: React.ComponentType) {
  return () => (
    <TabShell>
      <Page />
    </TabShell>
  );
}

/**
 * SPA navigations keep the previous scroll position by default, so opening
 * Profile/admin from a scrolled page landed mid-page with the top bar out
 * of view ("can't see the top menu, I scroll manually"). Reset to the top
 * whenever the PATH changes — in-page state changes (week nav, dialogs)
 * don't change the path and are unaffected.
 */
function ScrollToTopOnNavigate() {
  const [path] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [path]);
  return null;
}

export function Router() {
  return (
    <>
    <ScrollToTopOnNavigate />
    <Switch>
      <Route path={"/"} component={tabbed(Home)} />
      {/* `/home` is the old tab URL — redirect to the canonical `/`. */}
      <Route path={"/home"}>
        <Redirect to="/" />
      </Route>
      {/* `/canvas` (legacy single-page) DELETED in Stage B — falls through to
          the catch-all NotFound below. */}
      <Route path={"/team"} component={tabbed(Team)} />
      <Route path={"/team-report"} component={tabbed(TeamReport)} />
      <Route path={"/drafts"} component={tabbed(Drafts)} />
      {/* Phase 2A — Your Week: owner-only private reflection. Reached from a
          Home card (primary) and a More row (secondary); deliberately NOT a
          fifth bottom tab. */}
      <Route path={"/your-week"} component={tabbed(YourWeek)} />
      <Route path={"/more"} component={tabbed(More)} />
      <Route path={"/me"} component={tabbed(Profile)} />
      <Route path={"/admin/teams"} component={tabbed(AdminTeams)} />
      <Route path={"/admin/health"} component={tabbed(AdminHealth)} />
      <Route path={"/platform"} component={tabbed(PlatformConsole)} />
      <Route path={"/platform/health"} component={tabbed(AdminHealthCenter)} />
      <Route path={"/invite/:token"} component={InviteAccept} />
      <Route path={"/org-invite/:token"} component={OrgInviteAccept} />
      <Route path={"/privacy"} component={Privacy} />
      <Route path={"/terms"} component={Terms} />
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light" switchable>
        <I18nProvider>
          <TooltipProvider>
            {/* Mobile-polish: toasts sit bottom-center and are LIFTED above the
                fixed bottom tab bar + central Capture FAB (the shell reserves
                ~5.75rem for the nav; the FAB overhangs it), so a toast never
                overlaps the nav or the Capture button. Desktop keeps a small
                offset (no bottom nav there). */}
            <Toaster
              position="bottom-center"
              mobileOffset={{ bottom: "calc(6.5rem + env(safe-area-inset-bottom))" }}
            />
            <ActiveWorkspaceProvider>
              <GlobalDialogsProvider>
                <Router />
              </GlobalDialogsProvider>
            </ActiveWorkspaceProvider>
          </TooltipProvider>
        </I18nProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
